import { Cell, Workbook } from 'exceljs';

import { type BulkDonorScheduleInput } from './schema';

const EXCEL_COLUMN_MAPPING = {
  'Account Code': 'accountCode',
  'Bill Account': 'billAccountCode',
  Breed: 'donorBreed',
  'Donor ID': 'donorNameToMatch',
  Registration: 'donorRegistrationNumber',
  'Pedigree Name': 'pedigreeName',
  Pregnant: 'pregnant',
  'Birth Date': 'birthDate',
  'Program Name': 'programName',
  TPI: 'tpi',
  'Net Merit': 'nm',
  'OPU Program': 'opuProgram',
  Tattoo: 'tattoo',
  'Received Date': 'received',
  'Yard Code': 'yardCode',
  Sire: 'sireNameToMatch',
  'Sire Breed': 'sireBreed',
  'Sire Registration': 'sireRegistrationNumber',
  'Collection Code': 'collectionCode',
  'Semen Type': 'semenType',
  NAAB: 'naab',
  Cane: 'cane',
  Location: 'donorLocation',
  Schedule: 'scheduleCode',
  Pool: 'pool',
  'Aspiration Comment': 'aspirationComment',
  Ship: 'ship',
  'Shipment Group': 'shipmentGroup',
  Freeze: 'freeze',
  'Freeze Method': 'freezeMethod',
  Export: 'export',
  TT: 'trypsinTreat',
  'Non EU Country': 'nonEuCountry',
} as const satisfies Record<string, keyof BulkDonorScheduleInput>;

export async function parseExcelFile(
  file: File,
): Promise<BulkDonorScheduleInput[]> {
  const workbook = new Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = workbook.getWorksheet(1);
  if (!worksheet) {
    throw new Error('No worksheet found');
  }

  // Parse headers from first row
  const headerRow = worksheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell((cell, colNumber) => {
    headers[colNumber - 1] = cell.text?.trim() || '';
  });

  // Validate required headers
  const requiredHeaders = [
    'Account Code',
    'Breed',
    'Donor ID',
  ] satisfies (keyof typeof EXCEL_COLUMN_MAPPING)[];
  const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
  }

  const data: BulkDonorScheduleInput[] = [];

  // Process data rows (skip header row)
  for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
    const row = worksheet.getRow(rowNumber);
    const rowData: any = {};

    let hasData = false;
    row.eachCell((cell, colNumber) => {
      const header = headers[colNumber - 1];
      const fieldName =
        EXCEL_COLUMN_MAPPING[header as keyof typeof EXCEL_COLUMN_MAPPING];

      if (fieldName) {
        const value = processCellValue(cell, fieldName);
        if (value !== undefined && value !== null && value !== '') {
          hasData = true;
        }
        rowData[fieldName] = value;
      }
    });

    if (hasData) {
      data.push(rowData);
    }
  }

  return data;
}

function processCellValue(
  cell: Cell,
  fieldName: string,
): string | number | undefined {
  const value = cell.value;

  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  // Boolean fields
  if (['pregnant', 'pool', 'trypsinTreat'].includes(fieldName)) {
    const strValue = String(value).toUpperCase();
    return strValue === 'Y' || strValue === 'y'
      ? 'Y'
      : strValue === 'N' || strValue === 'n'
        ? 'N'
        : '';
  }

  // Number fields
  if (['tpi', 'nm'].includes(fieldName)) {
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
  }

  // String fields
  return String(value).trim();
}
